import { Fragment } from 'react';
import classNames from 'classnames';
import { Popover, Transition } from '@headlessui/react';

import { useMainMachine } from '../../../utils/useMainMachine';
import { ACTIONS } from '../../../state/actions';
import { GENERATING } from '../../constants';

import BalloonIcon from '@crazyegginc/hatch/dist/images/logo-ce-balloon.svg?react';
import TriangleIcon from '@crazyegginc/hatch/dist/images/icon-triangle-filled.svg?react';

import HomeIcon from '@crazyegginc/hatch/dist/images/icon-home-filled.svg?react';
import WebanalyticIcon from '@crazyegginc/hatch/dist/images/icon-web-analytic.svg?react';
import LiveIcon from '@crazyegginc/hatch/dist/images/icon-list-outline.svg?react';
import GoalIcon from '@crazyegginc/hatch/dist/images/icon-goal-filled.svg?react';
import SnapshotIcon from '@crazyegginc/hatch/dist/images/icon-camera-filled.svg?react';
import RecordingIcon from '@crazyegginc/hatch/dist/images/icon-recording-filled.svg?react';
import ErrorIcon from '@crazyegginc/hatch/dist/images/icon-warning-filled.svg?react';
import TrafficIcon from '@crazyegginc/hatch/dist/images/icon-chart-filled.svg?react';
import FunnelIcon from '@crazyegginc/hatch/dist/images/icon-filter-filled.svg?react';
import ABTestIcon from '@crazyegginc/hatch/dist/images/icon-ab-test-outline.svg?react';
import SurveyIcon from '@crazyegginc/hatch/dist/images/icon-survey-filled.svg?react';
import CTAIcon from '@crazyegginc/hatch/dist/images/icon-click-outline.svg?react';
import HelpIcon from '@crazyegginc/hatch/dist/images/icon-help-filled.svg?react';

const redirectBase =
  process.env.NODE_ENV === 'production' ? 'https://core.crazyegg.com' : 'https://core.crazyeggdev.com';

export function Menu() {
  const { useSelector } = useMainMachine();
  const capabilities = useSelector(({ context }) => context.capabilities);
  const needsAuth = useSelector(({ context }) => !context.userId);
  const generating = useSelector((state) => state.hasTag(GENERATING));

  const showHome = capabilities?.home.permissions.nav.allowed ?? false;
  const showWebAnalytics = capabilities?.webAnalytics.permissions.nav.allowed ?? true;
  const showLive = capabilities?.liveActivity.permissions.nav.allowed ?? true;
  const showGoals = capabilities?.goals.permissions.nav.allowed ?? true;
  const showSnapshots = capabilities?.snapshots.permissions.nav.allowed ?? true;
  const showRecordings = capabilities?.recordings.permissions.nav.allowed ?? true;
  const showErrors = capabilities?.errors.permissions.nav.allowed ?? true;
  const showTrafficAnalysis = capabilities?.trafficAnalysis.permissions.nav.allowed ?? true;
  const showFunnels = capabilities?.funnels.permissions.nav.allowed ?? false;
  const showABTesting = capabilities?.abTesting.permissions.nav.allowed ?? true;
  const showSurveys = capabilities?.surveys.permissions.nav.allowed ?? true;
  const showCTAs = capabilities?.ctas.permissions.nav.allowed ?? true;

  return (
    <Popover>
      {({ open }) => (
        <div className="relative mr-3.5 ml-1.25">
          <Popover.Button
            className={classNames(
              'w-[57px] h-[50px] relative rounded-md',
              'flex items-center justify-center hover:enabled:bg-gray-700',
              'outline-none focus:outline-none focus-visible:outline-white focus-visible:outline-dashed',
              'disabled:cursor-not-allowed',
            )}
            aria-label="crazy egg menu"
            disabled={needsAuth && generating}
          >
            <BalloonIcon className="fill-current w-8 h-8 mr-2.5" />
            <TriangleIcon
              className={classNames('h-2 w-2 fill-current absolute right-2 mt-2', {
                'rotate-180': open,
              })}
            />
          </Popover.Button>
          <Transition
            appear={true}
            as={Fragment}
            leave="transition-opacity ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            enter="transition-opacity ease-in duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
          >
            <Popover.Panel className="absolute z-10 bg-gray-900 bottom-full mb-2.5 rounded-[5px] p-1.25 shadow-md flex flex-col w-[200px]">
              {({ close }) => (
                <>
                  {showHome ? (
                    <Item
                      Icon={HomeIcon}
                      title="Home"
                      target={`${redirectBase}/home`}
                      needsAuth={needsAuth}
                      close={close}
                    />
                  ) : null}
                  {showWebAnalytics ? (
                    <Item
                      Icon={WebanalyticIcon}
                      title="Web Analytics"
                      target={`${redirectBase}/web-analytics`}
                      needsAuth={needsAuth}
                      close={close}
                    />
                  ) : null}
                  {showLive ? (
                    <Item
                      Icon={LiveIcon}
                      title="Live Activity"
                      target={`${redirectBase}/live`}
                      needsAuth={needsAuth}
                      close={close}
                    />
                  ) : null}
                  {showGoals ? (
                    <Item
                      Icon={GoalIcon}
                      title="Goals"
                      target={`${redirectBase}/goals`}
                      needsAuth={needsAuth}
                      close={close}
                    />
                  ) : null}
                  {showSnapshots ? (
                    <Item
                      Icon={SnapshotIcon}
                      title="Snapshots"
                      target={`${redirectBase}/snapshots`}
                      needsAuth={needsAuth}
                      close={close}
                    />
                  ) : null}
                  {showRecordings ? (
                    <Item
                      Icon={RecordingIcon}
                      title="Recordings"
                      target={`${redirectBase}/recordings`}
                      needsAuth={needsAuth}
                      close={close}
                    />
                  ) : null}
                  {showErrors ? (
                    <Item
                      Icon={ErrorIcon}
                      title="Errors"
                      target={`${redirectBase}/errors`}
                      needsAuth={needsAuth}
                      close={close}
                    />
                  ) : null}
                  {showTrafficAnalysis ? (
                    <Item
                      Icon={TrafficIcon}
                      title="Traffic"
                      target={`${redirectBase}/traffic`}
                      needsAuth={needsAuth}
                      close={close}
                    />
                  ) : null}
                  {showFunnels ? (
                    <Item
                      Icon={FunnelIcon}
                      title="Funnel"
                      target={`${redirectBase}/funnel`}
                      needsAuth={needsAuth}
                      close={close}
                    />
                  ) : null}
                  {showABTesting ? (
                    <Item
                      Icon={ABTestIcon}
                      title="A/B Testing"
                      target={`${redirectBase}/ab-tests`}
                      needsAuth={needsAuth}
                      close={close}
                    />
                  ) : null}
                  {showSurveys ? (
                    <Item
                      Icon={SurveyIcon}
                      title="Surveys"
                      target={`${redirectBase}/addons?type=SURVEYS`}
                      needsAuth={needsAuth}
                      close={close}
                    />
                  ) : null}
                  {showCTAs ? (
                    <Item
                      Icon={CTAIcon}
                      title="CTAs"
                      target={`${redirectBase}/addons?type=CTAS`}
                      needsAuth={needsAuth}
                      close={close}
                    />
                  ) : null}
                  <div className="bg-gray-300 h-px -mx-1.25 my-2.5" />
                  <Item
                    Icon={HelpIcon}
                    title="Contact Support"
                    target="https://support.crazyegg.com/hc/en-us"
                    needsAuth={false}
                    close={close}
                  />
                </>
              )}
            </Popover.Panel>
          </Transition>
        </div>
      )}
    </Popover>
  );
}

export function Item({ Icon, title, target, needsAuth, close }) {
  const { machine } = useMainMachine();

  function onClick() {
    if (needsAuth) {
      machine.send({
        type: ACTIONS.SHOW_AUTH_MODAL,
        redirect: target,
        onImmediateAuthSuccess: () => window.open(target, '_blank'),
      });
    } else {
      window.open(target, '_blank');
    }
    close();
  }

  return (
    <button
      type="button"
      className={classNames('flex items-center pl-3.75 text-white text-base h-10 hover:bg-blue-500 rounded')}
      onClick={onClick}
    >
      <Icon className="fill-current w-4 h-4 mr-2.5" />
      {title}
    </button>
  );
}
